'use client';

import { Play } from 'lucide-react';
import Image from 'next/image';
import { type CSSProperties, type SyntheticEvent, useMemo } from 'react';
import { MediaGrid } from '@/components/tweet/TweetMediaGrid';
import { isSupportedImageUrl } from '@/lib/media/image-support';
import { constructPreviewUrl, withPhotoSize } from '@/lib/twitter/media-utils';
import { cn } from '@/lib/utils';
import type { UIMediaItem } from '@/types/ui-media';

interface TweetMediaStaticProperties {
  mediaItems: UIMediaItem[];
}

const markImageLoaded = (event: SyntheticEvent<HTMLImageElement>) => {
  // biome-ignore lint/complexity/useLiteralKeys: dataset keys require bracket access for strict TS.
  event.currentTarget.dataset['loaded'] = 'true';
};

const markImageErrored = (event: SyntheticEvent<HTMLImageElement>) => {
  // biome-ignore lint/complexity/useLiteralKeys: dataset keys require bracket access for strict TS.
  event.currentTarget.dataset['loaded'] = 'true';
};

const computeMaxHeight = (mediaCount: number, aspectRatio: number) => {
  if (mediaCount > 1) {
    return null;
  }
  if (aspectRatio > 2) {
    return '300px';
  }
  if (aspectRatio > 1.5) {
    return '400px';
  }
  if (aspectRatio > 0.8) {
    return '500px';
  }
  return '600px';
};

const renderPhotoMedia = (media: UIMediaItem, forceAspect: boolean | undefined, mediaCount: number) => {
  if (!media.url) {
    return null;
  }
  const aspectRatio = media.width && media.height ? media.width / media.height : 16 / 9;
  const sizedUrl = withPhotoSize(media.url, 'large');
  const maxHeight = computeMaxHeight(mediaCount, aspectRatio);
  const style: CSSProperties | undefined = forceAspect
    ? undefined
    : {
        aspectRatio,
        ...(maxHeight ? { maxHeight } : {}),
      };
  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden bg-muted',
        !forceAspect && 'rounded-lg',
        forceAspect && 'aspect-[16/9]'
      )}
    >
      <Image
        src={sizedUrl}
        alt={media.altText || 'Tweet image'}
        width={media.width || 400}
        height={media.height || 300}
        loading="lazy"
        decoding="async"
        unoptimized
        className={cn(
          'block w-full object-cover transition-all duration-200',
          forceAspect ? 'h-full' : 'h-auto',
          'data-[loaded=false]:scale-[1.02] data-[loaded=false]:blur-sm'
        )}
        style={style}
        data-loaded="false"
        onLoad={markImageLoaded}
        onError={markImageErrored}
      />
    </div>
  );
};

const renderVideoMedia = (media: UIMediaItem, forceAspect: boolean | undefined, mediaCount: number) => {
  const aspectRatio = media.width && media.height ? media.width / media.height : 16 / 9;
  const previewUrl = media.previewImageUrl ?? undefined;
  const synthesizedPreview = previewUrl && media.mediaKey ? previewUrl === constructPreviewUrl(media.mediaKey) : false;
  const resolvedPreviewUrl = synthesizedPreview ? undefined : previewUrl;
  const maxHeight = computeMaxHeight(mediaCount, aspectRatio);
  const style: CSSProperties | undefined = forceAspect
    ? undefined
    : {
        aspectRatio,
        ...(maxHeight ? { maxHeight } : {}),
      };
  return (
    <div className={cn('relative h-full w-full overflow-hidden bg-muted', !forceAspect && 'rounded-lg')} style={style}>
      {resolvedPreviewUrl ? (
        <Image
          src={resolvedPreviewUrl}
          alt={media.altText || 'Video thumbnail'}
          width={media.width || 400}
          height={media.height || 300}
          loading="lazy"
          decoding="async"
          unoptimized
          className={cn(
            'block w-full object-cover',
            forceAspect ? 'h-full' : 'h-auto',
            'data-[loaded=false]:scale-[1.02] data-[loaded=false]:blur-sm'
          )}
          data-loaded="false"
          onLoad={markImageLoaded}
          onError={markImageErrored}
        />
      ) : (
        <div
          className={cn(
            'flex h-full w-full items-center justify-center bg-muted/70',
            forceAspect ? '' : 'min-h-[220px]'
          )}
        >
          <span className="text-xs text-muted-foreground">Video</span>
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="rounded-full bg-black/60 p-3 text-white">
          <Play className="h-5 w-5" />
        </div>
      </div>
      {media.type === 'animated_gif' && (
        <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
          GIF
        </div>
      )}
    </div>
  );
};

const renderMedia = (media: UIMediaItem, _index: number, forceAspect: boolean | undefined, mediaCount: number) => {
  if (media.type === 'video' || media.type === 'animated_gif') {
    return renderVideoMedia(media, forceAspect, mediaCount);
  }
  return renderPhotoMedia(media, forceAspect, mediaCount);
};

export function TweetMediaStatic({ mediaItems }: TweetMediaStaticProperties) {
  const items = useMemo(() => sanitizeMediaItems(mediaItems), [mediaItems]);
  const mediaCount = items.length;

  if (mediaCount === 0) {
    return null;
  }
  const renderSingleMedia = (media: UIMediaItem, index: number, forceAspect?: boolean) =>
    renderMedia(media, index, forceAspect, mediaCount);

  return (
    <div className="mt-3" data-no-conversation>
      <MediaGrid mediaItems={items} renderSingleMedia={renderSingleMedia} />
    </div>
  );
}

function sanitizeMediaItems(mediaItems: UIMediaItem[]): UIMediaItem[] {
  const seen = new Set<string>();
  const out: UIMediaItem[] = [];
  for (const item of mediaItems) {
    if (!item) continue;
    const photoHasValidUrl = item.url && isSupportedImageUrl(item.url);
    if (item.type === 'photo' && !photoHasValidUrl) {
      continue;
    }
    const key = item.mediaKey || `${item.type}|${item.url || ''}|${item.previewImageUrl || ''}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(item);
  }
  return out;
}
